import { z } from "zod";
import type { TablesInsert } from "@/types/database";
import {
  segmentRulesToJson,
  type Segment,
  type SegmentRule,
} from "@/types/spyserp";
import { SEGMENT_COLORS, type SegmentColor } from "@/constants/segmentColors";

const keywordRuleSchema = z.object({
  match: z.enum(["contains", "exact"]),
  value: z.string(),
});

export const segmentFormSchema = z.object({
  name: z.string().trim().min(1, "Name is required"),
  color: z.enum(SEGMENT_COLORS),
  target_type: z.enum(["domain", "url"]),
  keywords: z.array(keywordRuleSchema),
  tlds: z.string(),
  locales: z.string(),
});

export type SegmentFormValues = z.infer<typeof segmentFormSchema>;

export const emptySegmentFormValues: SegmentFormValues = {
  name: "",
  color: "blue",
  target_type: "domain",
  keywords: [],
  tlds: "",
  locales: "",
};

const toSegmentColor = (color: string): SegmentColor =>
  (SEGMENT_COLORS as readonly string[]).includes(color)
    ? (color as SegmentColor)
    : "blue";

export const segmentToFormValues = (seg: Segment): SegmentFormValues => ({
  name: seg.name,
  color: toSegmentColor(seg.color),
  target_type: seg.target_type,
  keywords: (seg.rules.keywords ?? []).map((kw) => ({
    match: kw.match === "exact" ? "exact" : "contains",
    value: kw.value,
  })),
  tlds: (seg.rules.tlds ?? []).join(", "),
  locales: (seg.rules.locales ?? []).join(", "),
});

export const formValuesToSegmentRules = (
  values: SegmentFormValues,
): SegmentRule => {
  const rules: SegmentRule = {};

  const keywords = values.keywords.filter((k) => k.value.trim() !== "");
  if (keywords.length > 0) rules.keywords = keywords;

  const parsedTlds = values.tlds
    .split(",")
    .map((s) => s.trim().replace(".", ""))
    .filter((s) => s);
  if (parsedTlds.length > 0) rules.tlds = parsedTlds;

  const parsedLocales = values.locales
    .split(",")
    .map((s) => s.trim())
    .filter((s) => s);
  if (parsedLocales.length > 0) rules.locales = parsedLocales;

  return rules;
};

export const formValuesToPayload = (
  values: SegmentFormValues,
): TablesInsert<"spyserp_segments"> => ({
  name: values.name,
  color: values.color,
  target_type: values.target_type,
  rules: segmentRulesToJson(formValuesToSegmentRules(values)),
});
